import React, { useState } from 'react';
import { UserProfile, ClubEvent, Attendance, RankRequirement } from '../../types';
import { 
  Calendar, 
  MessageSquare, 
  Trophy, 
  User, 
  HelpCircle, 
  CheckCircle2, 
  Clock, 
  MapPin, 
  Phone, 
  Mail, 
  ArrowRight,
  ShieldCheck,
  Sparkles,
  ExternalLink,
  ChevronRight,
  HeartHandshake
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

interface SimpleDashboardProps {
  currentUser: UserProfile | null;
  events: ClubEvent[];
  attendance: Attendance[];
  ranks: RankRequirement[];
  onNavigateTab: (tab: string) => void;
  onToggleSimpleMode: () => void;
  onOpenHelp: () => void;
  onRsvpEvent?: (eventId: string, status: 'attending' | 'not-attending') => void;
}

export default function SimpleDashboard({
  currentUser,
  events,
  attendance,
  ranks,
  onNavigateTab,
  onToggleSimpleMode,
  onOpenHelp,
  onRsvpEvent
}: SimpleDashboardProps) {
  const [activeSection, setActiveSection] = useState<'home' | 'events' | 'comms' | 'profile' | 'help'>('home');

  // Next upcoming event
  const upcomingEvents = events
    .filter(e => e.startDate >= new Date().toISOString().split('T')[0])
    .sort((a, b) => a.startDate.localeCompare(b.startDate));
  const nextEvent = upcomingEvents[0] || null;

  // Current user rank & points
  const userRank = ranks.find(r => r.id === currentUser?.currentRank)?.name || currentUser?.currentRank || 'Crewman';
  const userPoints = currentUser?.manualPoints || 0;

  // Check if current user is RSVP'd to next event
  const isRsvpd = attendance.some(a => a.eventId === nextEvent?.id && a.userId === currentUser?.uid && a.status === 'attending');

  return (
    <div className="min-h-screen bg-[#0d1117] text-white flex flex-col font-sans pb-24 md:pb-8">
      {/* Top Mobile-Friendly Header */}
      <header className="bg-[#161b22] border-b border-[#30363d] px-4 py-3 sticky top-0 z-30 flex justify-between items-center shadow-md">
        <div>
          <h1 className="text-lg md:text-xl font-black tracking-wide text-[#58a6ff] flex items-center gap-2">
            USS ZAVALA
            <span className="text-[0.65rem] bg-[#238636] text-white px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
              Easy View
            </span>
          </h1>
          <p className="text-xs text-gray-400">
            {currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Welcome, Guest'}
          </p>
        </div>

        <button
          onClick={onToggleSimpleMode}
          className="bg-[#21262d] hover:bg-[#30363d] border border-[#30363d] text-[#f0883e] text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-1.5 transition-colors shadow-sm"
        >
          <span>🚀</span>
          <span className="hidden sm:inline">Switch to</span> LCARS Mode
        </button>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-3xl w-full mx-auto p-4 space-y-5">
        
        {/* User Greeting & Status Card */}
        <div className="bg-gradient-to-r from-[#1f6feb]/20 to-[#238636]/10 border-2 border-[#1f6feb]/40 rounded-2xl p-5 shadow-lg">
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <span className="text-xs font-bold text-[#58a6ff] uppercase tracking-wider">
                Crew Member Status
              </span>
              <h2 className="text-2xl font-black text-white">
                {currentUser?.firstName || 'Friend'} {currentUser?.lastName}
              </h2>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <span className="bg-[#1f6feb] text-white font-bold text-xs px-3 py-1 rounded-full flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  {userRank}
                </span>
                <span className="bg-[#d29922]/20 border border-[#d29922]/40 text-[#f0883e] font-bold text-xs px-3 py-1 rounded-full flex items-center gap-1">
                  <Trophy className="w-3.5 h-3.5" />
                  {userPoints} Promotion Points
                </span>
              </div>
            </div>
            <div className="w-12 h-12 rounded-full bg-[#1f6feb]/30 border-2 border-[#58a6ff] flex items-center justify-center text-xl font-black shrink-0">
              {currentUser?.firstName?.charAt(0) || 'Z'}
            </div>
          </div>
        </div>

        {/* Action Card 1: Next Event & 1-Tap RSVP */}
        <div className="bg-[#161b22] border-2 border-[#30363d] rounded-2xl p-5 space-y-3 shadow-md">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-[#d29922]/20 rounded-xl text-[#f0883e]">
                <Calendar className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-base md:text-lg text-white">
                Next Upcoming Meeting
              </h3>
            </div>
            <button
              onClick={() => onNavigateTab('events')}
              className="text-xs font-bold text-[#58a6ff] hover:underline flex items-center gap-1"
            >
              See All <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {nextEvent ? (
            <div className="bg-[#0d1117] border border-[#30363d] rounded-xl p-4 space-y-3">
              <div>
                <h4 className="font-black text-lg text-white">{nextEvent.name}</h4>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs text-gray-400 mt-1">
                  <span className="flex items-center gap-1.5 font-bold text-[#58a6ff]">
                    <Clock className="w-3.5 h-3.5" />
                    {nextEvent.startDate}
                  </span>
                  {nextEvent.location && (
                    <span className="flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-gray-500" />
                      {nextEvent.location}
                    </span>
                  )}
                </div>
              </div>

              {nextEvent.description && (
                <p className="text-xs text-gray-300 leading-relaxed line-clamp-2">
                  {nextEvent.description}
                </p>
              )}

              {/* 1-Tap RSVP Buttons for Non-Tech Users */}
              <div className="pt-2 border-t border-[#30363d] flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
                <span className="text-xs text-gray-400 font-bold">
                  Are you attending?
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (onRsvpEvent && nextEvent.id) onRsvpEvent(nextEvent.id, 'attending');
                      toast.success(`RSVP confirmed for "${nextEvent.name}"!`);
                    }}
                    className={`flex-1 sm:flex-none px-5 py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-all shadow-md ${
                      isRsvpd
                        ? 'bg-[#238636] text-white ring-2 ring-[#3fb950]'
                        : 'bg-[#238636]/20 border border-[#238636] text-[#3fb950] hover:bg-[#238636] hover:text-white'
                    }`}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {isRsvpd ? "I'M ATTENDING ✓" : "I'M GOING"}
                  </button>
                  <button
                    onClick={() => {
                      if (onRsvpEvent && nextEvent.id) onRsvpEvent(nextEvent.id, 'not-attending');
                      toast.info(`Updated RSVP to Not Attending.`);
                    }}
                    className="px-4 py-2.5 rounded-xl font-bold text-xs bg-[#21262d] text-gray-400 hover:text-white hover:bg-[#30363d]"
                  >
                    CAN'T MAKE IT
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-400 py-3">No upcoming events scheduled at this moment.</p>
          )}
        </div>

        {/* Action Card 2: Ship Announcements & Messages */}
        <div className="bg-[#161b22] border-2 border-[#30363d] rounded-2xl p-5 space-y-3 shadow-md">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-[#1f6feb]/20 rounded-xl text-[#58a6ff]">
                <MessageSquare className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-base md:text-lg text-white">
                Ship Announcements & Chat
              </h3>
            </div>
            <button
              onClick={() => onNavigateTab('comms')}
              className="text-xs font-bold text-[#58a6ff] hover:underline flex items-center gap-1"
            >
              Open Chat <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <p className="text-xs text-gray-300">
            Read the latest messages from Command and join the conversation in Ten Forward.
          </p>

          <button
            onClick={() => onNavigateTab('comms')}
            className="w-full bg-[#1f6feb] hover:bg-[#388bfd] text-white font-bold py-3 px-4 rounded-xl text-xs flex items-center justify-center gap-2 transition-colors shadow-md"
          >
            <MessageSquare className="w-4 h-4" />
            READ SHIP ANNOUNCEMENTS & CHAT
          </button>
        </div>

        {/* Action Card 3: Convention Shifts & Volunteering */}
        <div className="bg-[#161b22] border-2 border-[#30363d] rounded-2xl p-5 space-y-3 shadow-md">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-[#a371f7]/20 rounded-xl text-[#d2a8ff]">
                <HeartHandshake className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-base md:text-lg text-white">
                Convention Shifts & Helping Out
              </h3>
            </div>
            <button
              onClick={() => onNavigateTab('con-schedule')}
              className="text-xs font-bold text-[#58a6ff] hover:underline flex items-center gap-1"
            >
              View Board <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <p className="text-xs text-gray-300">
            Support the chapter at upcoming comic cons and public events by signing up for fan table shifts.
          </p>

          <button
            onClick={() => onNavigateTab('con-schedule')}
            className="w-full bg-[#21262d] hover:bg-[#30363d] border border-[#30363d] text-white font-bold py-3 px-4 rounded-xl text-xs flex items-center justify-center gap-2 transition-colors shadow-sm"
          >
            <HeartHandshake className="w-4 h-4 text-[#d2a8ff]" />
            VIEW AVAILABLE VOLUNTEER SHIFTS
          </button>
        </div>

        {/* Action Card 4: Need Help or Have Questions? */}
        <div className="bg-[#161b22] border-2 border-[#30363d] rounded-2xl p-5 space-y-3 shadow-md">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-[#238636]/20 rounded-xl text-[#3fb950]">
              <HelpCircle className="w-5 h-5" />
            </div>
            <h3 className="font-bold text-base md:text-lg text-white">
              Need Help or Have Questions?
            </h3>
          </div>

          <p className="text-xs text-gray-300">
            If you need assistance navigating the app, reporting an issue, or learning how promotion points work:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <button
              onClick={onOpenHelp}
              className="bg-[#238636] hover:bg-[#2ea043] text-white font-bold py-3 px-4 rounded-xl text-xs flex items-center justify-center gap-2 shadow-md transition-colors"
            >
              <HelpCircle className="w-4 h-4" />
              ASK A QUESTION / GET HELP
            </button>

            <a
              href="mailto:contact@usszavala.org"
              className="bg-[#21262d] hover:bg-[#30363d] border border-[#30363d] text-white font-bold py-3 px-4 rounded-xl text-xs flex items-center justify-center gap-2 transition-colors"
            >
              <Mail className="w-4 h-4 text-[#58a6ff]" />
              EMAIL THE CHAPTER
            </a>
          </div>
        </div>

      </main>

      {/* Bottom Thumb-Friendly Navigation Bar (Mobile) */}
      <nav className="fixed bottom-0 left-0 right-0 bg-[#161b22] border-t border-[#30363d] px-2 py-2 z-40 flex justify-around items-center shadow-2xl">
        <button
          onClick={() => { setActiveSection('home'); onNavigateTab('comms'); }}
          className="flex flex-col items-center gap-1 p-2 text-white font-bold text-[0.65rem] hover:text-[#58a6ff]"
        >
          <Calendar className="w-5 h-5 text-[#58a6ff]" />
          <span>Home</span>
        </button>

        <button
          onClick={() => onNavigateTab('events')}
          className="flex flex-col items-center gap-1 p-2 text-white font-bold text-[0.65rem] hover:text-[#58a6ff]"
        >
          <Clock className="w-5 h-5 text-[#d29922]" />
          <span>Events</span>
        </button>

        <button
          onClick={() => onNavigateTab('comms')}
          className="flex flex-col items-center gap-1 p-2 text-white font-bold text-[0.65rem] hover:text-[#58a6ff]"
        >
          <MessageSquare className="w-5 h-5 text-[#58a6ff]" />
          <span>Chat</span>
        </button>

        <button
          onClick={() => onNavigateTab('profile')}
          className="flex flex-col items-center gap-1 p-2 text-white font-bold text-[0.65rem] hover:text-[#58a6ff]"
        >
          <User className="w-5 h-5 text-[#3fb950]" />
          <span>My Profile</span>
        </button>

        <button
          onClick={onOpenHelp}
          className="flex flex-col items-center gap-1 p-2 text-white font-bold text-[0.65rem] hover:text-[#58a6ff]"
        >
          <HelpCircle className="w-5 h-5 text-[#f0883e]" />
          <span>Help</span>
        </button>
      </nav>
    </div>
  );
}
